// Простейший grid-мир для DDA-raycaster'а.
// Каждая ячейка: 0 = пусто, >0 = ID текстуры стены.
// Реальные сектора с разной высотой пола/потолка можно добавить позже —
// для MVP достаточно цельной арены с одинаковой высотой.

import type { ChunkRegistry } from "./chunks";
import { COLUMN_EMPTY_BOTTOM, COLUMN_EMPTY_TOP, ensureColumnMaps } from "./chunks";
import { ChunkRole } from "../net/protocol";

export const TILE = 1.0;          // размер клетки в мировых единицах

/** Зеркало server BuildGridFromWalls: StepUpMax + 0.01. */
const GRID_COLUMN_TOP_THRESHOLD = 0.26;
export const WORLD_HEIGHT = 3.0;  // высота потолка
/** Зеркало server/internal/game/world.go PlayerRadius. */
export const PLAYER_RADIUS = 0.30;
export const EYE_HEIGHT = 1.6;

/** Зеркало server game.MeshWallAABBMinHeight: mesh_collision wall/window AABB
 *  с высотой меньше этого — вырожденная «крышка» фундамента (пол), не стена. */
export const MESH_WALL_AABB_MIN_HEIGHT = 0.02;

/** Зеркало server game.WallGridMaxZSpans / WallGridZSpanStride. */
export const WALL_GRID_MAX_Z_SPANS = 10;
export const WALL_GRID_Z_SPAN_STRIDE = WALL_GRID_MAX_Z_SPANS * 2;

function clearWallCellZSpans(buf: Float32Array, idx: number): void {
  const off = idx * WALL_GRID_Z_SPAN_STRIDE;
  for (let i = 0; i < WALL_GRID_MAX_Z_SPANS; i++) {
    buf[off + 2 * i] = Infinity;
    buf[off + 2 * i + 1] = -Infinity;
  }
}

function addWallZSpan(buf: Float32Array, idx: number, lo: number, hi: number): void {
  if (!(lo < hi)) return;
  const off = idx * WALL_GRID_Z_SPAN_STRIDE;
  const sp: { lo: number; hi: number }[] = [];
  for (let i = 0; i < WALL_GRID_MAX_Z_SPANS; i++) {
    const a = buf[off + 2 * i];
    const b = buf[off + 2 * i + 1];
    if (a < b) sp.push({ lo: a, hi: b });
  }
  sp.push({ lo, hi });
  sp.sort((u, v) => u.lo - v.lo);
  const m: { lo: number; hi: number }[] = [];
  for (const s of sp) {
    if (m.length === 0) {
      m.push({ lo: s.lo, hi: s.hi });
    } else {
      const last = m[m.length - 1];
      if (s.lo <= last.hi + 1e-4) {
        if (s.hi > last.hi) last.hi = s.hi;
      } else {
        m.push({ lo: s.lo, hi: s.hi });
      }
    }
  }
  const touchTol = 1e-3;
  while (m.length > WALL_GRID_MAX_Z_SPANS) {
    let merged = false;
    for (let i = 0; i < m.length - 1; i++) {
      const gap = m[i + 1].lo - m[i].hi;
      if (gap <= touchTol) {
        if (m[i + 1].hi > m[i].hi) m[i].hi = m[i + 1].hi;
        m.splice(i + 1, 1);
        merged = true;
        break;
      }
    }
    if (merged) continue;
    let narrow = 0;
    let narrowH = m[0].hi - m[0].lo;
    for (let i = 1; i < m.length; i++) {
      const h = m[i].hi - m[i].lo;
      if (h < narrowH) {
        narrowH = h;
        narrow = i;
      }
    }
    m.splice(narrow, 1);
  }
  for (let i = 0; i < WALL_GRID_MAX_Z_SPANS; i++) {
    if (i < m.length) {
      buf[off + 2 * i] = m[i].lo;
      buf[off + 2 * i + 1] = m[i].hi;
    } else {
      buf[off + 2 * i] = Infinity;
      buf[off + 2 * i + 1] = -Infinity;
    }
  }
}

/** Зеркало server wallCellCylinderHitsAnyZSpan. */
export function wallCellCylinderHitsAnyZSpan(
  buf: Float32Array,
  idx: number,
  feetZ: number,
  headZ: number,
): boolean {
  const off = idx * WALL_GRID_Z_SPAN_STRIDE;
  // Пустой набор — false (зеркало сервера): спаны пишут только воксельные
  // столбцы; solid-тайл без спанов = mesh-стена, её точную коллизию даёт
  // collidesMeshWallAABBs, а не fast-path.
  for (let i = 0; i < WALL_GRID_MAX_Z_SPANS; i++) {
    const lo = buf[off + 2 * i];
    const hi = buf[off + 2 * i + 1];
    if (!(lo < hi)) continue;
    if (headZ > lo && feetZ < hi) return true;
  }
  return false;
}

export interface GridMap {
  width: number;
  height: number;
  /** длина = width*height, индекс = x + y*width */
  cells: Uint8Array;
  /** мировой минимум клетки (0,0); у level-pack = bounds чанков */
  originX?: number;
  originY?: number;
  /** размер клетки в метрах (обычно 1) */
  cellSize?: number;
  /** До WALL_GRID_MAX_Z_SPANS пар (lo,hi) на клетку, упаковано в Float32Array
   *  длины width*height*WALL_GRID_Z_SPAN_STRIDE (зеркало server CellWallZSpans).
   *  Несколько полос — когда vox_wall и mesh_collision не пересекаются по Z.
   *  Не задано у arena1 — 2D solid. */
  cellWallZSpans?: Float32Array;
}

const ARENA_LAYOUT: string[] = [
  "1111111111111111",
  "1..............1",
  "1..2222....3...1",
  "1..2..2....3...1",
  "1..2..2....3...1",
  "1..2222....3...1",
  "1..............1",
  "1......11......1",
  "1......11......1",
  "1..............1",
  "1...4444.......1",
  "1...4..4.......1",
  "1...4..4.......1",
  "1...4444.......1",
  "1..............1",
  "1111111111111111",
];

export function buildArena(): GridMap {
  const height = ARENA_LAYOUT.length;
  const width = ARENA_LAYOUT[0].length;
  const cells = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    const row = ARENA_LAYOUT[y];
    for (let x = 0; x < width; x++) {
      const ch = row[x];
      cells[x + y * width] = ch === "." ? 0 : parseInt(ch, 10);
    }
  }
  return { width, height, cells, originX: 0, originY: 0, cellSize: 1 };
}

export function worldToCell(map: GridMap, wx: number, wy: number): [number, number] {
  const cs = map.cellSize ?? 1;
  const ox = map.originX ?? 0;
  const oy = map.originY ?? 0;
  return [Math.floor((wx - ox) / cs), Math.floor((wy - oy) / cs)];
}

export function tileWorldBounds(map: GridMap, tx: number, ty: number): [number, number, number, number] {
  const cs = map.cellSize ?? 1;
  const ox = map.originX ?? 0;
  const oy = map.originY ?? 0;
  const x0 = ox + tx * cs;
  const y0 = oy + ty * cs;
  return [x0, y0, x0 + cs, y0 + cs];
}

export function isSolid(map: GridMap, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= map.width || y >= map.height) return true;
  return map.cells[x + y * map.width] !== 0;
}

/** Скользящие коллизии круга радиуса r против сетки. Простая ось-за-осью версия. */
export function moveAndSlide(
  map: GridMap,
  px: number, py: number,
  dx: number, dy: number,
  r = PLAYER_RADIUS,
): [number, number] {
  let nx = px + dx;
  const [tx0, ty0] = worldToCell(map, nx + Math.sign(dx) * r, py - r);
  const [tx1, ty1] = worldToCell(map, nx + Math.sign(dx) * r, py + r);
  if (isSolid(map, tx0, ty0) || isSolid(map, tx1, ty1)) {
    nx = px;
  }
  let ny = py + dy;
  const [tx2, ty2] = worldToCell(map, nx - r, ny + Math.sign(dy) * r);
  const [tx3, ty3] = worldToCell(map, nx + r, ny + Math.sign(dy) * r);
  if (isSolid(map, tx2, ty2) || isSolid(map, tx3, ty3)) {
    ny = py;
  }
  return [nx, ny];
}

/** XY AABB всех чанков (мировые метры). */
export function chunkWorldXYBounds(registry: ChunkRegistry): {
  minX: number; minY: number; maxX: number; maxY: number;
} {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const c of registry.all()) {
    minX = Math.min(minX, c.ox);
    minY = Math.min(minY, c.oy);
    maxX = Math.max(maxX, c.ox + c.sx * c.cubeSize);
    maxY = Math.max(maxY, c.oy + c.sy * c.cubeSize);
  }
  if (!Number.isFinite(minX)) {
    return { minX: 0, minY: 0, maxX: TILE * 16, maxY: TILE * 16 };
  }
  return { minX, minY, maxX, maxY };
}

/** Совпадает с level.json + server BuildGridFromWalls (origin = bounds.min). */
export interface LevelGridManifestBounds {
  bounds: [[number, number, number], [number, number, number]];
  gridCellSize: number;
  /** GLB mesh_collision из level.json — те же AABB, что BuildGridFromWalls на сервере. */
  meshCollision?: MeshCollisionEntry[];
}

/** Совпадает с server level.json `mesh_collision`. */
export interface MeshCollisionEntry {
  role: string;
  min: [number, number, number];
  max: [number, number, number];
  object_name?: string;
  /** Единичный XY, направление подъёма (build_level); иначе эвристика по AABB. */
  stair_climb_xy?: [number, number];
  /** Растер Z по ступеням (build_level); fallback после raycast'а stair_tris. */
  stair_heightfield?: {
    ox: number;
    oy: number;
    cell: number;
    nx: number;
    ny: number;
    z: number[];
  };
  /** Триангль-soup для вертикального raycast'а (первичный путь у stair). */
  sliced?: boolean;
  stair_tris?: {
    ox: number;
    oy: number;
    cell: number;
    nx: number;
    ny: number;
    tri_count: number;
    verts_b64: string;
    bin_offsets_b64: string;
    bin_indices_b64: string;
  };
}

/** GridMap из wall-чанков (как server BuildGridFromWalls). Для level-pack сцен.
 *  `manifest` — из /api/level-manifest: те же bounds/grid_cell_size, что у сервера;
 *  без него сетка по hull чанков (индексы тайлов могли не совпадать с сервером).
 *  Вычитание: floor — центр клетки внутри AABB пола; stair — пересечение клетки
 *  с AABB лестницы (крыльцо без «дырок» на границе тайла). */
export function buildGridFromWallChunks(
  registry: ChunkRegistry,
  manifest?: LevelGridManifestBounds | null,
  gridCellSize = TILE,
): GridMap {
  let minX: number;
  let minY: number;
  let width: number;
  let height: number;
  let g: number;
  if (
    manifest?.bounds &&
    manifest.bounds.length === 2 &&
    Number.isFinite(manifest.bounds[0][0]) &&
    Number.isFinite(manifest.bounds[1][0])
  ) {
    const b = manifest.bounds;
    minX = b[0][0];
    minY = b[0][1];
    g = manifest.gridCellSize > 0 ? manifest.gridCellSize : gridCellSize;
    width = Math.max(1, Math.floor((b[1][0] - b[0][0]) / g));
    height = Math.max(1, Math.floor((b[1][1] - b[0][1]) / g));
  } else {
    const b = chunkWorldXYBounds(registry);
    minX = b.minX;
    minY = b.minY;
    g = gridCellSize;
    width = Math.max(1, Math.round((b.maxX - minX) / g));
    height = Math.max(1, Math.round((b.maxY - minY) / g));
  }
  const cells = new Uint8Array(width * height);
  const nCells = width * height;
  const cellWallZSpans = new Float32Array(nCells * WALL_GRID_Z_SPAN_STRIDE);
  for (let i = 0; i < nCells; i++) clearWallCellZSpans(cellWallZSpans, i);
  const thr = GRID_COLUMN_TOP_THRESHOLD;

  for (const ch of registry.all()) {
    if (ch.role !== ChunkRole.Wall) continue;
    ensureColumnMaps(ch);
    if (ch.columnTopZ === null || ch.columnBottomZ === null) continue;
    const topZ = ch.columnTopZ;
    const botZ = ch.columnBottomZ;
    const sx = ch.sx;
    const sy = ch.sy;
    const vcs = ch.cubeSize;
    for (let by = 0; by < sy; by++) {
      for (let bx = 0; bx < sx; bx++) {
        const t = topZ[bx + by * sx];
        if (t === COLUMN_EMPTY_TOP || t < thr) continue;
        const minWx = ch.ox + bx * vcs;
        const maxWx = ch.ox + (bx + 1) * vcs;
        const minWy = ch.oy + by * vcs;
        const maxWy = ch.oy + (by + 1) * vcs;
        const txMin = Math.floor((minWx - minX) / g);
        const txMax = Math.floor((maxWx - 1e-4 - minX) / g);
        const tyMin = Math.floor((minWy - minY) / g);
        const tyMax = Math.floor((maxWy - 1e-4 - minY) / g);
        const b = botZ[bx + by * sx];
        let colZ0 = ch.oz;
        if (b < COLUMN_EMPTY_BOTTOM) {
          colZ0 = b;
        }
        const colZ1 = t;
        for (let ty = tyMin; ty <= tyMax; ty++) {
          for (let tx = txMin; tx <= txMax; tx++) {
            if (tx >= 0 && ty >= 0 && tx < width && ty < height) {
              const idx = tx + ty * width;
              cells[idx] = 1;
              addWallZSpan(cellWallZSpans, idx, colZ0, colZ1);
            }
          }
        }
      }
    }
  }
  const meshBoxes = manifest?.meshCollision;
  if (meshBoxes && meshBoxes.length > 0) {
    for (const mc of meshBoxes) {
      const role = mc.role;
      if (role !== "wall" && role !== "window") continue;
      const minWx = mc.min[0];
      const maxWx = mc.max[0];
      const minWy = mc.min[1];
      const maxWy = mc.max[1];
      const minWz = mc.min[2];
      const maxWz = mc.max[2];
      // Вырожденные по высоте боксы (крышка фундамента) — не стены.
      if (maxWz - minWz < MESH_WALL_AABB_MIN_HEIGHT) continue;
      const txMin = Math.floor((minWx - minX) / g);
      const txMax = Math.floor((maxWx - 1e-4 - minX) / g);
      const tyMin = Math.floor((minWy - minY) / g);
      const tyMax = Math.floor((maxWy - 1e-4 - minY) / g);
      for (let ty = tyMin; ty <= tyMax; ty++) {
        for (let tx = txMin; tx <= txMax; tx++) {
          if (tx >= 0 && ty >= 0 && tx < width && ty < height) {
            const idx = tx + ty * width;
            // Solid без Z-спана (зеркало сервера): mesh-стена, задевшая тайл
            // на пару сантиметров, блокировала весь тайл 1×1 м в fast-path —
            // точную коллизию mesh-стен даёт collidesMeshWallAABBs по AABB.
            cells[idx] = 1;
          }
        }
      }
    }
    const meshCollisionFloorGridSubtractMaxArea = 4000;
    const clearCell = (tx: number, ty: number): void => {
      if (tx < 0 || ty < 0 || tx >= width || ty >= height) return;
      const idx = tx + ty * width;
      cells[idx] = 0;
      clearWallCellZSpans(cellWallZSpans, idx);
    };
    for (const mc of meshBoxes) {
      if (mc.role !== "floor" && mc.role !== "stair") continue;
      const minWx = mc.min[0];
      const maxWx = mc.max[0];
      const minWy = mc.min[1];
      const maxWy = mc.max[1];
      const dx = maxWx - minWx;
      const dy = maxWy - minWy;
      if (dx <= 0 || dy <= 0) continue;
      if (dx * dy > meshCollisionFloorGridSubtractMaxArea) continue;
      const txMin = Math.floor((minWx - minX) / g);
      const txMax = Math.floor((maxWx - 1e-4 - minX) / g);
      const tyMin = Math.floor((minWy - minY) / g);
      const tyMax = Math.floor((maxWy - 1e-4 - minY) / g);
      if (mc.role === "floor") {
        for (let ty = tyMin; ty <= tyMax; ty++) {
          for (let tx = txMin; tx <= txMax; tx++) {
            if (tx < 0 || ty < 0 || tx >= width || ty >= height) continue;
            const cx = minX + (tx + 0.5) * g;
            const cy = minY + (ty + 0.5) * g;
            if (cx < minWx || cx > maxWx || cy < minWy || cy > maxWy) continue;
            clearCell(tx, ty);
          }
        }
        continue;
      }
      for (let ty = tyMin; ty <= tyMax; ty++) {
        for (let tx = txMin; tx <= txMax; tx++) {
          if (tx < 0 || ty < 0 || tx >= width || ty >= height) continue;
          const cellX0 = minX + tx * g;
          const cellX1 = minX + (tx + 1) * g;
          const cellY0 = minY + ty * g;
          const cellY1 = minY + (ty + 1) * g;
          if (cellX0 >= maxWx || cellX1 <= minWx || cellY0 >= maxWy || cellY1 <= minWy) continue;
          clearCell(tx, ty);
        }
      }
    }
    // Mirror server BuildGridFromWalls: floor/stair subtract removes broad
    // wall-AABB interiors, then restores thin local wall/window boxes so floors
    // cannot erase real perimeter walls near doors/porches.
    for (const mc of meshBoxes) {
      const role = mc.role;
      if (role !== "wall" && role !== "window") continue;
      const minWx = mc.min[0];
      const maxWx = mc.max[0];
      const minWy = mc.min[1];
      const maxWy = mc.max[1];
      const minWz = mc.min[2];
      const maxWz = mc.max[2];
      if (maxWz - minWz < MESH_WALL_AABB_MIN_HEIGHT) continue;
      const dx = maxWx - minWx;
      const dy = maxWy - minWy;
      if (dx > g * 1.25 && dy > g * 1.25) continue;
      const txMin = Math.floor((minWx - minX) / g);
      const txMax = Math.floor((maxWx - 1e-4 - minX) / g);
      const tyMin = Math.floor((minWy - minY) / g);
      const tyMax = Math.floor((maxWy - 1e-4 - minY) / g);
      for (let ty = tyMin; ty <= tyMax; ty++) {
        for (let tx = txMin; tx <= txMax; tx++) {
          if (tx >= 0 && ty >= 0 && tx < width && ty < height) {
            const idx = tx + ty * width;
            // Как и в основном mesh-проходе: solid без Z-спана (см. выше).
            cells[idx] = 1;
          }
        }
      }
    }
  }
  return { width, height, cells, originX: minX, originY: minY, cellSize: g, cellWallZSpans };
}

/** Выбор сетки: legacy arena1 остаётся buildArena; level-pack — из стен. */
export function pickClientGrid(
  sceneId: string | undefined,
  registry: ChunkRegistry,
  manifest?: LevelGridManifestBounds | null,
): GridMap {
  const span = chunkWorldXYBounds(registry);
  const spanX = span.maxX - span.minX;
  const spanY = span.maxY - span.minY;
  if ((sceneId === "arena1" || sceneId === undefined) && spanX <= 17 && spanY <= 17 && spanX >= 14 && spanY >= 14) {
    return buildArena();
  }
  return buildGridFromWallChunks(registry, manifest);
}
